import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  ClipboardText,
  Handshake,
  SignOut,
  House,
  Users,
  Database,
  UserGear,
  UserPlus,
  Key,
  Trash,
  ShieldCheck
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTeam = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const [showResetFor, setShowResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState('');

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/admins`);
      setAdmins(res.data);
    } catch (e) {
      toast.error('Failed to load admin list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/admins`, createForm);
      toast.success('Admin created');
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '' });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!showResetFor) return;
    if (resetPwd.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/admins/${showResetFor.id}/password`, { new_password: resetPwd });
      toast.success(`Password reset for ${showResetFor.email}`);
      setShowResetFor(null);
      setResetPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const deleteAdmin = async (target) => {
    if (!window.confirm(`Remove admin access from ${target.email}? This deletes their admin login.`)) return;
    try {
      await axios.delete(`${API}/admin/admins/${target.id}`);
      toast.success('Admin removed');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove admin');
    }
  };

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Heart weight="fill" className="w-6 h-6 text-primary" />
            <span className="font-serif text-xl text-white">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-stone-400 text-sm">Welcome, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-400 hover:text-white">
              <SignOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Nav */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6 overflow-x-auto">
            <Link to="/admin" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <House className="w-4 h-4 inline mr-2" />Overview
            </Link>
            <Link to="/admin/applications" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <ClipboardText className="w-4 h-4 inline mr-2" />Applications
            </Link>
            <Link to="/admin/database" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <Database className="w-4 h-4 inline mr-2" />Users
            </Link>
            <Link to="/admin/matching" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <Handshake className="w-4 h-4 inline mr-2" />Matching
            </Link>
            <Link to="/admin/team" className="py-4 text-white border-b-2 border-primary text-sm font-medium whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Admin Team
            </Link>
            <Link to="/admin/account" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <UserGear className="w-4 h-4 inline mr-2" />My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2" data-testid="admin-team-title">Admin Team</h1>
            <p className="text-stone-400 text-sm">Add trusted admins, reset their passwords, or remove access. Keep at least one extra admin so you're never locked out.</p>
          </div>
          <Button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="add-admin-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add Admin
          </Button>
        </div>

        {loading ? (
          <div className="text-stone-400 text-sm">Loading…</div>
        ) : (
          <div className="space-y-3">
            {admins.map((a) => {
              const isSelf = a.id === user?.id;
              return (
                <div
                  key={a.id}
                  className="bg-stone-800 rounded-lg p-5 flex items-center justify-between gap-4"
                  data-testid={`admin-row-${a.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <ShieldCheck weight="fill" className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{a.name}</span>
                        {isSelf && (
                          <span className="text-xs px-2 py-0.5 rounded bg-emerald-700/40 text-emerald-200">You</span>
                        )}
                      </div>
                      <div className="text-stone-400 text-sm">{a.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-stone-600 text-stone-200 hover:bg-stone-700"
                      onClick={() => { setShowResetFor(a); setResetPwd(''); }}
                      data-testid={`reset-admin-pw-${a.id}`}
                    >
                      <Key className="w-4 h-4 mr-1" /> Reset Password
                    </Button>
                    {!isSelf && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:bg-red-900/30 hover:text-red-300"
                        onClick={() => deleteAdmin(a)}
                        data-testid={`delete-admin-${a.id}`}
                      >
                        <Trash className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {admins.length === 0 && (
              <div className="text-stone-500 text-sm">No admins found.</div>
            )}
          </div>
        )}
      </main>

      {/* Create Admin Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add a New Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={createAdmin} className="space-y-4 py-2">
            <div>
              <Label className="text-stone-400 text-sm">Name</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                required
                className="bg-stone-700 border-stone-600 text-white mt-1"
                data-testid="new-admin-name-input"
              />
            </div>
            <div>
              <Label className="text-stone-400 text-sm">Email</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                required
                className="bg-stone-700 border-stone-600 text-white mt-1"
                data-testid="new-admin-email-input"
              />
            </div>
            <div>
              <Label className="text-stone-400 text-sm">Password (min 8 characters)</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
                required
                minLength={8}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                data-testid="new-admin-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="text-stone-400">Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="confirm-create-admin-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Create Admin
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetFor} onOpenChange={(o) => { if (!o) setShowResetFor(null); }}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Reset password for {showResetFor?.email}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4 py-2">
            <div>
              <Label className="text-stone-400 text-sm">New Password (min 8 characters)</Label>
              <Input
                type="password"
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                required
                minLength={8}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                data-testid="reset-pw-input"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowResetFor(null)} className="text-stone-400">Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="confirm-reset-pw-btn">
                <Key className="w-4 h-4 mr-2" /> Reset Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeam;
